import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Modal chrome shared by the new-UI dialogs. Presentation only: it owns the
 * panel scaffold (flex column, clipped) plus the colored header bar, an
 * optional sub-header row, a scrolling content area and the footer bar — all
 * with consistent paddings, colors and borders.
 *
 * It does NOT mount itself. The caller renders it inside a PrimeNG
 * DynamicDialog, a CDK overlay, etc., and sets the panel background, corner
 * radius and sizing via classes on the `<u2b-dialog>` element.
 *
 * Slots (projected by attribute):
 *   [u2bDialogHeader]    — contents of the colored header bar (title, actions)
 *   [u2bDialogSubheader] — optional row under the header (stepper, progress)
 *   (default)            — the scrolling body
 *   [u2bDialogFooter]    — contents of the footer bar (action buttons)
 *
 * Wrap multi-element header/footer content in a `class="contents"` element so
 * its children flow directly into the bar's flex layout.
 */
@Component({
    selector: 'u2b-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass],
    host: { class: 'flex flex-col min-h-0 overflow-hidden text-p-text' },
    template: `
        <header class="u2b-dialog-header flex-shrink-0 bg-p-primary text-p-primary-contrast flex items-center justify-between gap-4 px-6 py-5 flex-wrap">
            <ng-content select="[u2bDialogHeader]"></ng-content>
        </header>

        <ng-content select="[u2bDialogSubheader]"></ng-content>

        <section class="flex-1 min-h-0 overflow-y-auto px-6 py-5" [ngClass]="contentClass()">
            <ng-content></ng-content>
        </section>

        <footer class="flex-shrink-0 flex items-center justify-between gap-2 px-6 py-5 border-t border-p-border bg-p-surface-50">
            <ng-content select="[u2bDialogFooter]"></ng-content>
        </footer>
    `,
})
export class U2bDialogComponent {

    /** Extra classes for the scrolling content section (e.g. its background). */
    readonly contentClass = input<string>('');
}
